import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setAppError } from "../../../../store/userSlice";
import SmartDownloadPagination from "../../../../components/SmartDataTable/SmartDownloadPagination";
import UpdateValueSadaad from "./PopupUpdate/PopupUpdate";
import TermUpdatePopup from "./PopupUpdate/PopupUpdate";
import FilterDropDown from "../../../../ReusableComponents/FilterDropDown/FilterDropDown";
import { CgSpinner } from "react-icons/cg";
import nodeinstance from "../../../../nodeInstance";
import { checkAccessFn } from "../../../../utils/checkAccess";

export default function ListDataSadaad({
  token,
  updateData,
  setTrigger,
  trigger,
  selectedStatus,
  setSelectedStatus,
  selectVisaProvider,
  setSelectVisaProvider,
}) {
  const dispatch = useDispatch();
  const [selectedNationality, setSelectedNationality] = useState("");
  const [selectedReligion, setSelectedReligion] = useState("");
  const [selectedPOA, setSelectedPOA] = useState("");

  const dashboardData = useSelector((state) => state.dashboardData);
  const checkAccess = (module, name) =>
    checkAccessFn(dashboardData, module, name);

  const [showPopup, setShowPopup] = useState(false);
  const [showTermPopup, setShowTermPopup] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchData, setSearchData] = useState(null);

  // Notes state
  const [editingNotesId, setEditingNotesId] = useState(null);
  const [notesValue, setNotesValue] = useState("");
  const [notesLoading, setNotesLoading] = useState(false);

  // Check if any filter is applied to decide whether to skip initial load
  const isAnyFilterApplied = useMemo(() => {
    return (
      selectVisaProvider !== "" ||
      selectedStatus !== "" ||
      selectedPOA !== "" ||
      selectedNationality !== "" ||
      selectedReligion !== ""
    );
  }, [
    selectVisaProvider,
    selectedStatus,
    selectedPOA,
    selectedNationality,
    selectedReligion,
  ]);

  const handleStandaloneSearch = async (term) => {
    if (!term) return;
    setIsLoading(true);
    setIsSearchActive(true);
    try {
      const response = await nodeinstance.get(`/sadaad/search/${term}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data && response.data.status) {
        setSearchData(response.data.data);
      }
    } catch (error) {
      dispatch(setAppError({ msg: "Search failed: " + error.message }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDataLoad = useCallback((data, paginationState) => {
    setIsLoading(paginationState.isLoading);
    if (!data || data.length === 0) {
      setSelectedIds([]);
    }
  }, []);

  const handleSearchChange = (value) => {
    if (!value) {
      setIsSearchActive(false);
      setSearchData(null);
    }
  };

  const handleSelectionChange = useCallback((emp_id, isChecked) => {
    setSelectedIds((prev) => {
      const isAlreadySelected = prev.includes(emp_id);
      if (isChecked && !isAlreadySelected) {
        if (prev.length >= 25) {
          dispatch(
            setAppError({
              msg: "Maximum candidate selection limit reached",
            })
          );
          return prev;
        }
        return [...prev, emp_id];
      } else if (!isChecked && isAlreadySelected) {
        return prev.filter((id) => id !== emp_id);
      }
      return prev;
    });
  }, [dispatch]);

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const apiCall = () => {
    updateData();
    clearSelection();
  };

  // Notes handler
  const handleSaveNotes = useCallback(async (item) => {
    const trimmedNotes = notesValue.trim();

    // Validate length
    if (trimmedNotes.length > 2000) {
      dispatch(setAppError({ msg: "Notes cannot exceed 2000 characters" }));
      return;
    }

    setNotesLoading(true);
    try {
      const res = await nodeinstance.post("/sadaad/notes", {
        employeeId: item.emp_id,
        description: trimmedNotes,
        noteType: "SADAAD",
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res?.data?.status === true) {
        dispatch(setAppError({ msg: "Notes saved successfully", color: "success" }));
        setEditingNotesId(null);
        setNotesValue("");
        // Trigger data refresh
        setTrigger((prev) => !prev);
      } else {
        throw new Error(res?.data?.error || "Failed to save notes");
      }
    } catch (err) {
      dispatch(setAppError({ msg: err?.message || "Error saving notes" }));
    } finally {
      setNotesLoading(false);
    }
  }, [notesValue, token, dispatch, setTrigger]);

  const columns = useMemo(
    () => [
      { key: "serial", title: "S.NO." },
      { key: "emp_id", title: "EMPLOYEE ID" },
      { key: "notes", title: "NOTES" },
      { key: "sadaad_type", title: "Sadaad Type" },
      { key: "border_number", title: "BORDER NUMBER" },
      { key: "visa_provider", title: "VISA PROVIDER" },
      { key: "sponsor_id", title: "SPONSOR ID" },
      { key: "client_name", title: "CLIENT NAME" },
      { key: "iqama_applied_date", title: "IQAMA APPLIED DATE" },
      { key: "sadaad_number", title: "SADAAD NUMBER" },
      { key: "terms", title: "Term" },
      { key: "sadaad_status", title: "STATUS" },
      { key: "select", title: "" },
    ],
    []
  );

  const renderCustomCell = useCallback(
    (item, column, rowIndex, currentPage, pageSize) => {
      switch (column.key) {
        case "serial":
          return (currentPage - 1) * pageSize + rowIndex + 1;
        case "emp_id":
          return item.emp_id || "-";
        case "sadaad_type":
          return item.Iqamma_Sadaad === "Iqamma Renew Done"
            ? "SADAAD RENEWAL"
            : "NEW SADAAD";
        case "border_number":
        case "visa_provider":
        case "sponsor_id":
        case "client_name":
        case "iqama_applied_date":
        case "sadaad_number":
        case "terms":
        case "sadaad_status":
          return item[column.key] || "-";
        case "notes": {
          if (!checkAccess("SADAAD", "sadad-edit")) {
            return item.sadaad_notes || "-";
          }

          const isEditing = editingNotesId === item.emp_id;
          const hasNotes = item.sadaad_notes && item.sadaad_notes.trim() !== "";

          return (
            <div className="flex items-center gap-2">
              {isEditing ? (
                <>
                  <textarea
                    autoFocus
                    defaultValue={item.sadaad_notes || ""}
                    className="textarea textarea-sm textarea-bordered min-h-[60px] resize-none"
                    style={{ width: '300px' }}
                    placeholder="Enter notes (max 2000 characters)..."
                    maxLength={2000}
                    onChange={(e) => setNotesValue(e.target.value)}
                  />
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <button
                      className="btn btn-xs btn-success text-white px-3"
                      onClick={() => handleSaveNotes(item)}
                      disabled={notesLoading}
                    >
                      {notesLoading ? <CgSpinner className="animate-spin" /> : "Save"}
                    </button>
                    <button
                      className="btn btn-xs btn-ghost px-3"
                      onClick={() => setEditingNotesId(null)}
                      disabled={notesLoading}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : hasNotes ? (
                <>
                  <span className="line-clamp-2 max-w-[200px]" title={item.sadaad_notes}>
                    {item.sadaad_notes}
                  </span>
                  <button
                    className="btn btn-xs btn-primary px-3 whitespace-nowrap"
                    onClick={() => {
                      setEditingNotesId(item.emp_id);
                      setNotesValue(item.sadaad_notes || "");
                    }}
                  >
                    Edit
                  </button>
                </>
              ) : (
                <button
                  className="btn btn-xs btn-primary px-3 whitespace-nowrap"
                  onClick={() => {
                    setEditingNotesId(item.emp_id);
                    setNotesValue("");
                  }}
                >
                  Add
                </button>
              )}
            </div>
          );
        }
        case "select":
          if (
            item.sadaad_status === "Sadaad Completed" ||
            !checkAccess("SADAAD", "sadad-edit")
          ) {
            return <p>--</p>;
          }
          return (
            <div className="flex items-center justify-center">
              <input
                disabled={
                  selectedStatus === "" ||
                  selectVisaProvider === "" ||
                  item.sadaad_status === "Payment Pending"
                }
                type="checkbox"
                className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                onChange={(event) =>
                  handleSelectionChange(item.emp_id, event.target.checked)
                }
                checked={selectedIds.includes(item.emp_id)}
              />
            </div>
          );
        default:
          return item[column.key] || "-";
      }
    },
    [
      selectedIds,
      handleSelectionChange,
      selectedStatus,
      selectVisaProvider,
      checkAccess,
      editingNotesId,
      notesLoading,
      handleSaveNotes,
    ]
  );

  const downloadFile = async () => {
    setDownloadLoading(true);
    try {
      const response = await nodeinstance.get(
        `/sadaad/downloadSadaadNumberExcel/Sadaad`,
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.status === 200) {
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Sadaad`);
        document.body.appendChild(link);
        link.click();
        dispatch(
          setAppError({
            msg: "Excel Downloaded",
            color: "success",
          })
        );
        setDownloadLoading(false);
      } else {
        throw new Error("Something went wrong!");
      }
    } catch (err) {
      setDownloadLoading(false);
      dispatch(
        setAppError({
          msg: err.message,
        })
      );
    }
  };

  const headerLeft = (
    <div className="flex gap-2 justify-end items-center whitespace-nowrap">
      <FilterDropDown
        onChange={(e) => setSelectVisaProvider(e.target.value)}
        title={"Visa Provider"}
        url={`/sadaad/visa-providers/`}
        token={token}
        selectedStatus={selectVisaProvider}
      />
      <FilterDropDown
        onChange={(e) => setSelectedStatus(e.target.value)}
        title={"Client"}
        url={`/sadaad/clients`}
        token={token}
        selectedStatus={selectedStatus}
      />
      <FilterDropDown
        onChange={(e) => setSelectedPOA(e.target.value)}
        title={"POA"}
        url={`/sadaad/visa-providers/`}
        token={token}
        selectedStatus={selectedPOA}
      />
      <FilterDropDown
        onChange={(e) => setSelectedNationality(e.target.value)}
        title={"Nationality"}
        url={`/sadaad/Nationality`}
        token={token}
        selectedStatus={selectedNationality}
      />
      <FilterDropDown
        onChange={(e) => setSelectedReligion(e.target.value)}
        title={"Religion"}
        options={[
          { value: "MUSLIM", label: "MUSLIM" },
          { value: "NON MUSLIM", label: "NON MUSLIM" },
        ]}
        token={token}
        selectedStatus={selectedReligion}
      />
    </div>
  );

  const additionalActions = useMemo(() => {
    const actions = [];

    if (checkAccess("SADAAD", "sadad-edit")) {
      actions.push({
        label: "Update Sadaad",
        onClick: () => {
          if (selectedIds.length === 0) {
            dispatch(setAppError({ msg: "Please select Check box" }));
          } else {
            setShowPopup(true);
          }
        },
        disabled: false,
        className:
          "px-4 rounded-md py-2 text-sm h-[36px] text-white bg-blue cursor-pointer",
      });
    }

    actions.push(
      {
        label: downloadLoading ? (
          <div className="flex gap-2 items-center">
            <span>Downloading</span>
            <CgSpinner className="animate-spin" />
          </div>
        ) : (
          "Download Report"
        ),
        onClick: downloadFile,
        disabled: downloadLoading || isLoading,
        className: `px-4 rounded-md py-2 text-sm h-[36px] text-white bg-blue ${
          downloadLoading || isLoading ? "cursor-not-allowed opacity-50" : "cursor-pointer"
        }`,
      },
      {
        label: "Clear Filter",
        onClick: () => {
          setSelectVisaProvider("");
          setSelectedStatus("");
          setSelectedPOA("");
          setSelectedNationality("");
          setSelectedReligion("");
        },
        className:
          "px-4 rounded-md py-2 text-sm h-[36px] text-white bg-blue cursor-pointer",
      }
    );

    return actions;
  }, [
    checkAccess,
    selectedIds.length,
    dispatch,
    downloadLoading,
    isLoading,
    setSelectVisaProvider,
    setSelectedStatus,
    setSelectedPOA,
    setSelectedNationality,
    setSelectedReligion,
  ]);

  const apiEndpoint = useMemo(() => {
    const params = new URLSearchParams();
    if (selectVisaProvider) params.append("visa_provider", selectVisaProvider);
    if (selectedStatus) params.append("clientName", selectedStatus);
    if (selectedPOA) params.append("poa", selectedPOA);
    if (selectedNationality) params.append("nationality", selectedNationality);
    if (selectedReligion) params.append("religion", selectedReligion);
    
    return `/sadaad/GetSaddadbyfilter?${params.toString()}`;
  }, [selectVisaProvider, selectedStatus, selectedPOA, selectedNationality, selectedReligion]);

  return (
    <div className="mt-4">
      <SmartDownloadPagination
        apiEndpoint={apiEndpoint}
        token={token}
        columns={columns}
        layoutType="table"
        enableHighlighting={true}
        renderCustomCell={renderCustomCell}
        additionalActions={additionalActions}
        headerLeft={headerLeft}
        searchPlaceholder={"Search employee id..."}
        defaultPageSize={50}
        pageSizeOptions={[10, 50, 100, 500, 1000]}
        enableDownload={false}
        onDataLoad={handleDataLoad}
        trigger={trigger}
        skipInitialLoad={!isAnyFilterApplied && !isSearchActive}
        noDataMessage={
          !isAnyFilterApplied && !isSearchActive
            ? "Please apply a filter or search to see data"
            : "No data found"
        }
        onSearchClick={handleStandaloneSearch}
        onSearchChange={handleSearchChange}
        manualData={isSearchActive ? searchData : null}
      />

      {showPopup && (
        <UpdateValueSadaad
          list={selectedIds}
          PopUp={showPopup}
          setPopUp={setShowPopup}
          title={"Update Sadaad Number"}
          url={"/sadaad/update/"}
          apiCall={apiCall}
          setTrigger={setTrigger}
          trigger={trigger}
          isRenwal={false}
        />
      )}

      {showTermPopup && (
        <TermUpdatePopup
          list={selectedIds}
          PopUp={showTermPopup}
          setPopUp={setShowTermPopup}
          title={"Update Term"}
          url={"/sadaad/updateTerms/"}
          apiCall={apiCall}
        />
      )}
    </div>
  );
}